import { useLocation } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'

import {
  AccountTable,
  BulkEditTransactionsDialog,
  DeleteTransactionsDialog,
  EditTransactionDialog,
  OverviewSkeleton,
  TRANSACTIONS_TABLE_ANCHOR,
  type Transaction,
  TransactionsCharts,
  TransactionsStats,
  TransactionsSummarySkeleton,
  TransactionsTable,
  useDirectoryAccounts,
  useLoadingFirstTransactions,
  useLoadingMoreTransactions,
  useLoadingProject,
  useTransactions,
} from '@/modules/projects'
import { transactionFilterSchema } from '@/schemas/transactionFilterSchema'
import {
  applyFilters,
  type Filter,
  useFilters,
  useFiltersUrlSync,
} from '@/stores'

interface PageProps {
  slug: string
}

// The table reads the unfiltered list and applies filters internally; the chart reads the filtered list.
export function ProjectTransactionsPage({ slug }: PageProps) {
  useFiltersUrlSync()
  const transactions = useTransactions()
  const directoryAccounts = useDirectoryAccounts()
  const isLoadingProject = useLoadingProject()
  const isLoadingTransactions = useLoadingFirstTransactions()
  const isLoadingMoreTransactions = useLoadingMoreTransactions()
  const filters = useFilters()
  const { hash } = useLocation()
  // The directory is deliberately not gated on: the table renders with folded labels and relabels
  // itself when the directory lands, or keeps them if it never does.
  const isLoaded = !isLoadingProject && !isLoadingTransactions

  const filteredTransactions = useMemo((): Transaction[] => {
    if (!transactions || transactions.length === 0) return []

    const filterArray = filters as Filter[]
    if (!filterArray || filterArray.length === 0) {
      return transactions as Transaction[]
    }

    return applyFilters(
      transactions,
      filterArray,
      transactionFilterSchema,
    ) as Transaction[]
  }, [transactions, filters])

  // When arriving from the coverage grid's "View transactions", drop past the accounts and charts to
  // the ledger table. Deferred until the data loads, since the table isn't mounted while it's a skeleton.
  useEffect(() => {
    if (!isLoaded || hash.replace(/^#/, '') !== TRANSACTIONS_TABLE_ANCHOR)
      return
    document
      .getElementById(TRANSACTIONS_TABLE_ANCHOR)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [isLoaded, hash])

  if (!isLoaded) {
    return <OverviewSkeleton />
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <EditTransactionDialog />
      <DeleteTransactionsDialog />
      <BulkEditTransactionsDialog />
      {isLoadingMoreTransactions ? (
        <TransactionsSummarySkeleton accountCount={directoryAccounts.length} />
      ) : (
        <>
          <section className="overflow-hidden rounded-lg border bg-white">
            <h3 className="px-6 py-4 font-semibold leading-none tracking-tight">
              Accounts
            </h3>
            <AccountTable
              transactions={filteredTransactions}
              directoryAccounts={directoryAccounts}
            />
          </section>
          <TransactionsStats transactions={filteredTransactions} />
          <TransactionsCharts transactions={filteredTransactions} />
        </>
      )}
      <div
        id={TRANSACTIONS_TABLE_ANCHOR}
        className="flex-1 min-h-0 flex flex-col scroll-mt-4"
      >
        <TransactionsTable
          transactions={transactions}
          filterSchema={transactionFilterSchema}
          paginationKey={slug}
          dialogId="transactions"
          exportFilename={`${slug}-transactions`}
        />
      </div>
    </div>
  )
}
